/**
 * SubitizingQuestionScreen - K Subitizing Assessment
 *
 * Refactored to match PreK structure with shared hooks and components
 * while preserving K-specific business logic (quantity range 1-6).
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { PreKSettings as KSettings, DiagnosticAssessmentResult, DotPattern } from '../../types/kAssessmentTypes';
import { useKTTS, K_QUESTION_SCREEN_AUDIO } from '../../hooks/useKAudio';
import { useAuth } from '../../context/AuthContext';
import { progressQueue } from '../../services/ProgressQueueService';
import { recordPageTransition, completePreKAssessment } from '../../config/api';
import { FactStatus } from '../../types/progress';
import { TRACK_RANGES } from '../../types/constants';

// Shared components from PreKAssessment
import { AnswerChoicesGrid, AssessmentHeader, DotPatternDisplay, generateDotPattern } from '../PreKAssessment/shared';
import WarmupTransitionScreen from './WarmupTransitionScreen';
import ListenButton from '../common/ListenButton';

// ============================================================================
// K-Specific Constants
// ============================================================================

const TRACK_ID = 'TRACK_K_SUBITIZING';

// K uses quantities 1-6 (vs PreK 1-4)
const K_QUANTITY_RANGE = { min: 1, max: 6 };

// K answer choices range 0-7
const K_ANSWER_RANGE = { min: 0, max: 7 };

// Timer constants
const TIMER_CONSTANTS = {
  WALL_CLOCK_LIMIT_MS: 60000,
  QUESTION_TIME_LIMIT_MS: 6000,
  TICK_INTERVAL_MS: 100,
  CORRECT_ANSWER_DELAY_MS: 300,
  WRONG_ANSWER_FLASH_MS: 1000,
  QUESTION_READY_DELAY_MS: 500,
  SUBITIZING_FLASH_MS: 800,  // 800ms for K (shorter than PreK's 1000ms)
  SUBITIZING_BLANK_MS: 100,
};

// Warmup config
const WARMUP_CONFIG = {
  MAX_WARMUP_QUESTIONS: 3,
  TEST_TTS_INTERVAL: 3,
};

// Fluency target for K subitizing
const FLUENCY_TARGET_SEC = 6;

interface SubitizingQuestionScreenProps {
  settings: KSettings;
  onComplete: (results: DiagnosticAssessmentResult) => void;
}

const SubitizingQuestionScreen: React.FC<SubitizingQuestionScreenProps> = ({ settings, onComplete }) => {
  // ============================================================================
  // State Management
  // ============================================================================

  // Question tracking
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);

  // Warmup state
  const [isWarmup, setIsWarmup] = useState(true);
  const [warmupCount, setWarmupCount] = useState(0);
  const [showTransitionScreen, setShowTransitionScreen] = useState(false);

  // Subitizing-specific state
  const [currentPattern, setCurrentPattern] = useState<DotPattern | null>(null);
  const [flashPhase, setFlashPhase] = useState<'demo' | 'demo-flash' | 'demo-blank' | 'demo-answer' | 'intro' | 'ready' | 'flashing' | 'blank' | 'question'>('demo');
  const [showDots, setShowDots] = useState(false);
  const [introComplete, setIntroComplete] = useState(false);

  // Demo phase state (think-aloud modeling before assessment)
  const [demoComplete, setDemoComplete] = useState(false);
  const [demoPattern, setDemoPattern] = useState<DotPattern | null>(null);
  const [demoHighlightAnswer, setDemoHighlightAnswer] = useState(false);
  const [demoAnswerChoices, setDemoAnswerChoices] = useState<number[]>([]);

  // Answer state
  const [waitingForCorrectAnswer, setWaitingForCorrectAnswer] = useState(false);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [wrongAnswer, setWrongAnswer] = useState<number | null>(null);
  const [answerChoices, setAnswerChoices] = useState<number[]>([]);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);

  // Timer state
  const [wallClockTimeRemaining, setWallClockTimeRemaining] = useState(TIMER_CONSTANTS.WALL_CLOCK_LIMIT_MS);
  const [wallClockStarted, setWallClockStarted] = useState(false);
  const [activeTimeElapsed, setActiveTimeElapsed] = useState(0);
  const [questionTimeRemaining, setQuestionTimeRemaining] = useState(TIMER_CONSTANTS.QUESTION_TIME_LIMIT_MS);
  const [questionTimerActive, setQuestionTimerActive] = useState(false);
  const [timerActive, setTimerActive] = useState(false);

  // TTS state
  const [ttsHasPlayed, setTtsHasPlayed] = useState(false);

  // ============================================================================
  // Refs
  // ============================================================================

  const { user, updateUserField } = useAuth();
  const testStartTime = useRef<number>(Date.now());
  const questionStartTime = useRef<number>(Date.now());
  const timerActiveStartTime = useRef<number>(0);
  const accumulatedTimerTime = useRef<number>(0);
  const questionActiveStartRef = useRef<number | null>(null);
  const questionAccumulatedTimeRef = useRef<number>(0);
  const hasCompletedRef = useRef<boolean>(false);
  const isFirstQuestionRef = useRef(true);

  // TTS integration
  const { playTTSWithCallback, isTTSPlaying, playChime } = useKTTS();

  // Store generateNextPattern in a ref to avoid dependency issues
  const generateNextPatternRef = useRef<() => DotPattern>(() => ({
    id: 'placeholder',
    quantity: 1,
    dots: [],
  }));

  // Track if this is the first sequence (right after intro, so skip "get ready" audio)
  const isFirstSequenceRef = useRef(true);

  // ============================================================================
  // K-Specific Helper Functions
  // ============================================================================

  // Generate random quantity (1-6 for K level)
  const generateRandomQuantity = useCallback(() => {
    return Math.floor(Math.random() * (K_QUANTITY_RANGE.max - K_QUANTITY_RANGE.min + 1)) + K_QUANTITY_RANGE.min;
  }, []);

  // Create dot pattern for a quantity
  const createDotPattern = useCallback((quantity: number): DotPattern => {
    return {
      id: `pattern-${Date.now()}-${Math.random()}`,
      quantity,
      dots: generateDotPattern(quantity),
    };
  }, []);

  // Generate next pattern with different quantity
  const generateNextPattern = useCallback(() => {
    let newQuantity: number;
    do {
      newQuantity = generateRandomQuantity();
    } while (currentPattern && newQuantity === currentPattern.quantity);
    return createDotPattern(newQuantity);
  }, [currentPattern, generateRandomQuantity, createDotPattern]);

  // Keep ref updated
  generateNextPatternRef.current = generateNextPattern;

  // Quantity to fact mapping (quantities 1-6 map to facts from K subitizing range)
  const quantityToFact = useCallback((quantity: number): string => {
    const [startFact] = TRACK_RANGES.TRACK_K_SUBITIZING;
    return `FACT${startFact - 1 + quantity}`; // Maps 1-6 to facts 1623-1628
  }, []);

  // Determine fact status based on correctness and response time
  const determineFactStatus = useCallback((isCorrect: boolean, timeSpentMs: number): FactStatus => {
    if (!isCorrect) {
      return 'learning';
    }
    const timeSpentSeconds = timeSpentMs / 1000;
    if (timeSpentSeconds > FLUENCY_TARGET_SEC) {
      return 'accuracyPractice';
    } else {
      return 'mastered';
    }
  }, []);

  // Generate answer choices (target + 3 other options from 0-7)
  const generateAnswerChoices = useCallback((target: number): number[] => {
    const choices = [target];
    const used = new Set([target]);

    while (choices.length < 4) {
      const candidate = Math.floor(Math.random() * (K_ANSWER_RANGE.max - K_ANSWER_RANGE.min + 1)) + K_ANSWER_RANGE.min;
      if (!used.has(candidate)) {
        choices.push(candidate);
        used.add(candidate);
      }
    }

    return choices.sort(() => Math.random() - 0.5);
  }, []);

  // Handle test completion
  const handleTestComplete = useCallback(
    async (results: DiagnosticAssessmentResult) => {
      if (user?.userId) {
        try {
          await recordPageTransition({
            userId: user.userId,
            trackId: TRACK_ID,
            page: 'other',
          });
        } catch (error) {
          console.error('Error recording final page transition:', error);
        }

        try {
          const result = await completePreKAssessment(user.userId, TRACK_ID, {
            totalQuestions: results.totalQuestions || 0,
            correctAnswers: results.correctAnswers || 0,
            activeTimeElapsed: results.activeTimeElapsed || 0,
          });
          if (result.focusTrack) {
            updateUserField({ focusTrack: result.focusTrack });
          }
        } catch (error) {
          console.error('Failed to save K assessment data:', error);
        }
      }
      onComplete(results);
    },
    [user?.userId, onComplete, updateUserField]
  );

  // Reset question state
  const resetQuestionState = useCallback((callback?: () => void) => {
    isFirstQuestionRef.current = false;
    setTimerActive(false);
    setQuestionTimerActive(false);
    setQuestionTimeRemaining(TIMER_CONSTANTS.QUESTION_TIME_LIMIT_MS);
    setWrongAnswer(null);
    setShowDots(false);
    setTtsHasPlayed(false);
    setFlashPhase('ready');
    setHasAnswered(false);
    setSelectedAnswer(null);

    if (callback) {
      setTimeout(callback, 50);
    }
  }, []);

  // ============================================================================
  // Subitizing Sequence Logic
  // ============================================================================

  const startSubitizingSequence = useCallback(() => {
    setTtsHasPlayed(false);
    setTimerActive(false);

    // Function to start the flash sequence (with or without ready delay)
    const doFlash = () => {
      setFlashPhase('flashing');
      setShowDots(true);

      // Flash for 600ms
      setTimeout(() => {
        setShowDots(false);
        setFlashPhase('blank');

        // Brief blank period, then show question
        setTimeout(() => {
          setFlashPhase('question');
        }, TIMER_CONSTANTS.SUBITIZING_BLANK_MS);
      }, TIMER_CONSTANTS.SUBITIZING_FLASH_MS);
    };

    if (isFirstQuestionRef.current) {
      // First question: show "Get ready..." phase with delay
      setFlashPhase('ready');
      if (isFirstSequenceRef.current) {
        isFirstSequenceRef.current = false;
        setTimeout(doFlash, TIMER_CONSTANTS.QUESTION_READY_DELAY_MS);
      } else if (settings.speechEnabled) {
        playTTSWithCallback(K_QUESTION_SCREEN_AUDIO.subitizing.getReady, () => {
          setTimeout(doFlash, TIMER_CONSTANTS.QUESTION_READY_DELAY_MS);
        }).catch(() => {
          setTimeout(doFlash, TIMER_CONSTANTS.QUESTION_READY_DELAY_MS);
        });
      } else {
        setTimeout(doFlash, TIMER_CONSTANTS.QUESTION_READY_DELAY_MS);
      }
    } else {
      // Subsequent questions: skip ready phase, audio, and delay - flash immediately
      doFlash();
    }
  }, [settings.speechEnabled, playTTSWithCallback]);

  // ============================================================================
  // Effects
  // ============================================================================

  // Initialize first pattern
  useEffect(() => {
    if (!currentPattern) {
      const quantity = generateRandomQuantity();
      setCurrentPattern(createDotPattern(quantity));
    }
  }, [currentPattern, generateRandomQuantity, createDotPattern]);

  // Update answer choices when pattern changes
  useEffect(() => {
    if (currentPattern) {
      setAnswerChoices(generateAnswerChoices(currentPattern.quantity));
      setHasAnswered(false);
    }
  }, [currentPattern, generateAnswerChoices]);

  // Set track ID and report page transition on mount
  useEffect(() => {
    sessionStorage.setItem('activeTrackId', TRACK_ID);

    if (user?.userId) {
      recordPageTransition({
        userId: user.userId,
        trackId: TRACK_ID,
        page: 'assessment',
      }).catch((error: any) => {
        console.error('Error recording page transition:', error);
      });
    }
  }, [user?.userId]);

  // Update question start time when moving to test phase
  useEffect(() => {
    if (!isWarmup) {
      questionStartTime.current = Date.now();
      accumulatedTimerTime.current = 0;
      timerActiveStartTime.current = 0;
      questionAccumulatedTimeRef.current = 0;
    }
  }, [isWarmup, currentPattern]);

  // Track timer active time
  useEffect(() => {
    if (isWarmup) return;

    if (timerActive) {
      timerActiveStartTime.current = Date.now();
      questionActiveStartRef.current = Date.now();
    } else {
      if (timerActiveStartTime.current > 0) {
        const activeTime = Date.now() - timerActiveStartTime.current;
        accumulatedTimerTime.current += activeTime;
        timerActiveStartTime.current = 0;
      }
      if (questionActiveStartRef.current) {
        const questionActiveTime = Date.now() - questionActiveStartRef.current;
        questionAccumulatedTimeRef.current += questionActiveTime;
        questionActiveStartRef.current = null;
      }
    }
  }, [timerActive, isWarmup]);

  // Wall clock timer countdown
  useEffect(() => {
    if (isWarmup || !wallClockStarted || wallClockTimeRemaining <= 0) {
      if (
        wallClockTimeRemaining <= 0 &&
        wallClockStarted &&
        user?.userId &&
        !hasCompletedRef.current
      ) {
        hasCompletedRef.current = true;
        handleTestComplete({
          userId: user.userId,
          testResults: [],
          recommendedStartSkill: 1,
          placementReason: 'Time limit reached (60 seconds)',
          completedAt: new Date(),
          totalQuestions,
          correctAnswers,
          activeTimeElapsed,
        });
      }
      return;
    }

    const timer = setTimeout(() => {
      setWallClockTimeRemaining((prev) => Math.max(0, prev - TIMER_CONSTANTS.TICK_INTERVAL_MS));
    }, TIMER_CONSTANTS.TICK_INTERVAL_MS);

    return () => clearTimeout(timer);
  }, [
    wallClockTimeRemaining,
    wallClockStarted,
    isWarmup,
    handleTestComplete,
    user?.userId,
    totalQuestions,
    correctAnswers,
    activeTimeElapsed,
  ]);

  // Active time tracking
  useEffect(() => {
    if (isWarmup || !timerActive) return;

    const timer = setTimeout(() => {
      setActiveTimeElapsed((prev) => prev + TIMER_CONSTANTS.TICK_INTERVAL_MS);
    }, TIMER_CONSTANTS.TICK_INTERVAL_MS);

    return () => clearTimeout(timer);
  }, [timerActive, isWarmup, activeTimeElapsed]);

  // Play TTS when entering question phase
  useEffect(() => {
    if (flashPhase === 'question' && !ttsHasPlayed) {
      const shouldPlayAudio = isWarmup
        ? warmupCount === 0
        : currentQuestionIndex === 0;

      if (settings.speechEnabled && shouldPlayAudio) {
        playTTSWithCallback(K_QUESTION_SCREEN_AUDIO.subitizing.instruction, () => {
          if (!isWarmup) {
            setTimerActive(true);
            setQuestionTimerActive(true);
          }
          setTtsHasPlayed(true);
        }).catch(() => {
          if (!isWarmup) {
            setTimerActive(true);
            setQuestionTimerActive(true);
          }
          setTtsHasPlayed(true);
        });
      } else {
        if (!isWarmup) {
          setTimerActive(true);
          setQuestionTimerActive(true);
        }
        setTtsHasPlayed(true);
      }
    }
  }, [
    flashPhase,
    ttsHasPlayed,
    settings.speechEnabled,
    playTTSWithCallback,
    isWarmup,
    warmupCount,
    currentQuestionIndex,
  ]);

  // Handle replay button - replay audio instruction only (not dots, to preserve subitizing validity)
  const handleReplayAudio = useCallback(() => {
    playTTSWithCallback(K_QUESTION_SCREEN_AUDIO.subitizing.instruction).catch(() => {});
  }, [playTTSWithCallback]);

  // ============================================================================
  // Demo Phase Logic (think-aloud modeling before assessment)
  // ============================================================================

  // Initialize demo pattern and choices on mount
  useEffect(() => {
    if (!demoPattern && flashPhase === 'demo') {
      // Create a fixed pattern of 3 dots for demo
      const pattern = createDotPattern(3);
      setDemoPattern(pattern);
      setDemoAnswerChoices(generateAnswerChoices(3));
    }
  }, [demoPattern, flashPhase, createDotPattern, generateAnswerChoices]);

  // Handle demo phase sequence
  useEffect(() => {
    if (demoComplete) return;

    // Phase 1: Demo intro - play intro audio
    if (flashPhase === 'demo' && demoPattern) {
      if (settings.speechEnabled) {
        playTTSWithCallback(K_QUESTION_SCREEN_AUDIO.subitizing.demoIntro, () => {
          // After intro audio, start flash sequence
          setTimeout(() => {
            setFlashPhase('demo-flash');
            setShowDots(true);
          }, 500);
        }).catch(() => {
          setTimeout(() => {
            setFlashPhase('demo-flash');
            setShowDots(true);
          }, 500);
        });
      } else {
        // If speech disabled, wait briefly then start flash
        setTimeout(() => {
          setFlashPhase('demo-flash');
          setShowDots(true);
        }, 2000);
      }
    }

    // Phase 2: Flash dots for duration, then blank
    if (flashPhase === 'demo-flash' && showDots) {
      const timer = setTimeout(() => {
        setShowDots(false);
        setFlashPhase('demo-blank');
      }, TIMER_CONSTANTS.SUBITIZING_FLASH_MS);
      return () => clearTimeout(timer);
    }

    // Phase 3: Brief blank, then show answer
    if (flashPhase === 'demo-blank') {
      const timer = setTimeout(() => {
        setFlashPhase('demo-answer');
        setDemoHighlightAnswer(true);
        // Play "I saw 3" audio
        if (settings.speechEnabled) {
          playTTSWithCallback(K_QUESTION_SCREEN_AUDIO.subitizing.demoISaw3, () => {
            // After audio, brief pause then play "your turn"
            setTimeout(() => {
              playTTSWithCallback(K_QUESTION_SCREEN_AUDIO.subitizing.demoYourTurn, () => {
                // Demo complete, transition to warmup
                setTimeout(() => {
                  setDemoComplete(true);
                  setDemoHighlightAnswer(false);
                  setIntroComplete(true); // Skip regular intro
                  setFlashPhase('ready');
                }, 500);
              }).catch(() => {
                setTimeout(() => {
                  setDemoComplete(true);
                  setDemoHighlightAnswer(false);
                  setIntroComplete(true);
                  setFlashPhase('ready');
                }, 500);
              });
            }, 1000);
          }).catch(() => {
            setTimeout(() => {
              setDemoComplete(true);
              setDemoHighlightAnswer(false);
              setIntroComplete(true);
              setFlashPhase('ready');
            }, 2000);
          });
        } else {
          // If speech disabled, just pause and transition
          setTimeout(() => {
            setDemoComplete(true);
            setDemoHighlightAnswer(false);
            setIntroComplete(true);
            setFlashPhase('ready');
          }, 3000);
        }
      }, TIMER_CONSTANTS.SUBITIZING_BLANK_MS);
      return () => clearTimeout(timer);
    }
  }, [flashPhase, demoPattern, demoComplete, showDots, settings.speechEnabled, playTTSWithCallback]);

  // Handle intro phase - play intro audio then transition to ready
  // (This is now skipped when demo is complete)
  useEffect(() => {
    if (flashPhase === 'intro' && currentPattern && !introComplete) {
      if (settings.speechEnabled) {
        playTTSWithCallback(K_QUESTION_SCREEN_AUDIO.subitizing.assessmentIntro, () => {
          setIntroComplete(true);
          setFlashPhase('ready');
        }).catch(() => {
          setIntroComplete(true);
          setFlashPhase('ready');
        });
      } else {
        // If speech is disabled, still show intro briefly then proceed
        setTimeout(() => {
          setIntroComplete(true);
          setFlashPhase('ready');
        }, 2000);
      }
    }
  }, [flashPhase, currentPattern, introComplete, settings.speechEnabled, playTTSWithCallback]);

  // Start sequence when pattern changes and ready (but not during intro or demo)
  useEffect(() => {
    if (currentPattern && flashPhase === 'ready' && introComplete && demoComplete) {
      startSubitizingSequence();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPattern, introComplete, demoComplete, startSubitizingSequence]);

  // Per-question countdown timer
  useEffect(() => {
    if (isWarmup || !questionTimerActive || questionTimeRemaining <= 0 || hasAnswered) {
      if (
        questionTimeRemaining <= 0 &&
        questionTimerActive &&
        !isWarmup &&
        !hasAnswered
      ) {
        if (waitingForCorrectAnswer) {
          setWaitingForCorrectAnswer(false);
          const nextIndex = currentQuestionIndex + 1;
          setCurrentQuestionIndex(nextIndex);
          resetQuestionState(() => setCurrentPattern(generateNextPatternRef.current()));
        } else {
          setTotalQuestions((prev) => prev + 1);

          if (user?.userId && currentPattern) {
            try {
              const factId = quantityToFact(currentPattern.quantity);
              const factStatus = determineFactStatus(false, TIMER_CONSTANTS.QUESTION_TIME_LIMIT_MS);

              progressQueue.enqueueProgressUpdate(user.userId, {
                facts: {
                  [factId]: {
                    attempts: 1,
                    correct: 0,
                    timeSpent:
                      questionAccumulatedTimeRef.current +
                      (questionActiveStartRef.current
                        ? Date.now() - questionActiveStartRef.current
                        : 0),
                    status: factStatus,
                  },
                },
              });
            } catch (error) {
              console.error('Error reporting K subitizing timeout progress:', error);
            }
          }

          const nextIndex = currentQuestionIndex + 1;
          setCurrentQuestionIndex(nextIndex);
          resetQuestionState(() => setCurrentPattern(generateNextPatternRef.current()));
        }
      }
      return;
    }

    const timer = setTimeout(() => {
      setQuestionTimeRemaining((prev) => prev - TIMER_CONSTANTS.TICK_INTERVAL_MS);
    }, TIMER_CONSTANTS.TICK_INTERVAL_MS);

    return () => clearTimeout(timer);
  }, [
    questionTimeRemaining,
    questionTimerActive,
    isWarmup,
    waitingForCorrectAnswer,
    hasAnswered,
    currentQuestionIndex,
    currentPattern,
    user?.userId,
    quantityToFact,
    determineFactStatus,
    resetQuestionState,
  ]);

  // ============================================================================
  // Warmup Progression
  // ============================================================================

  const advanceWarmupQuestion = useCallback(() => {
    if (waitingForCorrectAnswer) {
      setWaitingForCorrectAnswer(false);
    }

    const newWarmupCount = warmupCount + 1;

    // Check if warmup is complete (fixed 3 questions)
    if (newWarmupCount >= WARMUP_CONFIG.MAX_WARMUP_QUESTIONS) {
      // Show transition screen instead of immediately starting test
      setShowTransitionScreen(true);
      return;
    }

    setWarmupCount(newWarmupCount);
    resetQuestionState(() => setCurrentPattern(generateNextPatternRef.current()));
  }, [waitingForCorrectAnswer, warmupCount, resetQuestionState]);

  // Handle transition screen completion - start the actual test
  const handleTransitionComplete = useCallback(() => {
    setShowTransitionScreen(false);
    setIsWarmup(false);
    setWarmupCount(0);
    setCurrentQuestionIndex(0);
    setWallClockStarted(true);
    resetQuestionState(() => setCurrentPattern(generateNextPatternRef.current()));
  }, [resetQuestionState]);

  // ============================================================================
  // Test Progression
  // ============================================================================

  const advanceTestQuestion = useCallback(() => {
    if (waitingForCorrectAnswer) {
      setWaitingForCorrectAnswer(false);
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);
      resetQuestionState(() => setCurrentPattern(generateNextPatternRef.current()));
      return;
    }

    setCorrectAnswers((prev) => prev + 1);
    setTotalQuestions((prev) => prev + 1);
    const nextIndex = currentQuestionIndex + 1;
    setCurrentQuestionIndex(nextIndex);
    resetQuestionState(() => setCurrentPattern(generateNextPatternRef.current()));
  }, [waitingForCorrectAnswer, currentQuestionIndex, resetQuestionState]);

  // ============================================================================
  // Answer Handler
  // ============================================================================

  const handleAnswerClick = useCallback(
    (selectedQuantity: number) => {
      if (!currentPattern) return;
      if (waitingForCorrectAnswer && selectedQuantity !== currentPattern.quantity) return;
      if (!waitingForCorrectAnswer && hasAnswered) return;

      if (!isWarmup) {
        setTimerActive(true);
        setQuestionTimerActive(true);
      }
      setTtsHasPlayed(true);
      setHasAnswered(true);
      setSelectedAnswer(selectedQuantity);

      const isCorrect = selectedQuantity === currentPattern.quantity;

      let timerActiveResponseTime = 0;
      if (!isWarmup) {
        timerActiveResponseTime =
          questionAccumulatedTimeRef.current +
          (questionActiveStartRef.current
            ? Date.now() - questionActiveStartRef.current
            : 0);
      }

      if (!isWarmup && user?.userId && !waitingForCorrectAnswer) {
        try {
          const factId = quantityToFact(currentPattern.quantity);
          const factStatus = determineFactStatus(isCorrect, timerActiveResponseTime);

          progressQueue.enqueueProgressUpdate(user.userId, {
            facts: {
              [factId]: {
                attempts: 1,
                correct: isCorrect ? 1 : 0,
                timeSpent: timerActiveResponseTime,
                status: factStatus,
              },
            },
          });
        } catch (error) {
          console.error('Error reporting K subitizing progress:', error);
        }
      }

      if (isWarmup) {
        if (isCorrect) {
          playChime().then(() => {
            setTimeout(() => {
              advanceWarmupQuestion();
            }, TIMER_CONSTANTS.CORRECT_ANSWER_DELAY_MS);
          });
        } else {
          setWrongAnswer(selectedQuantity);
          setTimeout(() => setWrongAnswer(null), TIMER_CONSTANTS.WRONG_ANSWER_FLASH_MS);

          if (settings.speechEnabled) {
            setTimeout(() => {
              playTTSWithCallback(K_QUESTION_SCREEN_AUDIO.subitizing.instruction).catch(() => {});
            }, TIMER_CONSTANTS.CORRECT_ANSWER_DELAY_MS);
          }

          setWaitingForCorrectAnswer(true);
          setHasAnswered(false);
        }
      } else {
        // Quiz mode: neutral feedback with chime for all answers, same timing
        if (!waitingForCorrectAnswer && !isCorrect) {
          setTotalQuestions((prev) => prev + 1);
        }

        playChime().then(() => {
          setTimeout(() => {
            if (isCorrect) {
              advanceTestQuestion();
            } else {
              const nextIndex = currentQuestionIndex + 1;
              setCurrentQuestionIndex(nextIndex);
              resetQuestionState(() => setCurrentPattern(generateNextPatternRef.current()));
            }
          }, TIMER_CONSTANTS.CORRECT_ANSWER_DELAY_MS);
        });
      }
    },
    [
      currentPattern,
      isWarmup,
      user,
      waitingForCorrectAnswer,
      hasAnswered,
      settings.speechEnabled,
      quantityToFact,
      determineFactStatus,
      playChime,
      playTTSWithCallback,
      advanceWarmupQuestion,
      advanceTestQuestion,
      currentQuestionIndex,
      resetQuestionState,
    ]
  );

  // ============================================================================
  // Render
  // ============================================================================

  if (!currentPattern) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        <div className="text-2xl">Loading...</div>
      </div>
    );
  }

  // Show transition screen after warmup completes
  if (showTransitionScreen) {
    return (
      <WarmupTransitionScreen
        onComplete={handleTransitionComplete}
        speechEnabled={settings.speechEnabled}
      />
    );
  }

  return (
    <div className="h-screen overflow-hidden bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col">
      {/* Header */}
      <AssessmentHeader
        isWarmup={isWarmup}
        showTimer={true}
        wallClockTimeRemaining={wallClockTimeRemaining}
        wallClockStarted={wallClockStarted}
        isTTSPlaying={isTTSPlaying}
        timerHideable={true}
      />

      {/* Listen Button - only during question phase, replays audio instruction only */}
      {settings.speechEnabled && flashPhase === 'question' && (
        <div className="flex justify-center pt-4">
          <ListenButton
            onClick={handleReplayAudio}
            disabled={hasAnswered}
            isPlaying={isTTSPlaying}
          />
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex items-center justify-center px-6 py-4 pb-8">
        <div className="w-full max-w-md lg:max-w-lg">
          {/* Demo Phase - Teacher modeling "Watch me!" */}
          {flashPhase === 'demo' && (
            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="mb-6"
              >
                <div className="text-6xl mb-4">👩‍🏫</div>
                <div className="text-2xl lg:text-3xl text-gray-700 font-semibold mb-2">
                  Watch me!
                </div>
                <div className="text-lg text-gray-500">
                  I'll show you how to find numbers...
                </div>
              </motion.div>
              {isTTSPlaying && (
                <div className="flex justify-center items-center text-gray-500">
                  <div className="animate-pulse mr-2">🔊</div>
                  <span>Listen carefully...</span>
                </div>
              )}
            </div>
          )}

          {/* Demo Flash Phase - Show demo dots */}
          {flashPhase === 'demo-flash' && showDots && demoPattern && (
            <DotPatternDisplay dots={demoPattern.dots} isVisible={true} />
          )}

          {/* Demo Blank Phase */}
          {flashPhase === 'demo-blank' && (
            <div className="grid grid-cols-2 gap-8 lg:gap-12">
              <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
              <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
              <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
              <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
            </div>
          )}

          {/* Demo Answer Phase - Show answer grid with "3" highlighted */}
          {flashPhase === 'demo-answer' && demoPattern && (
            <AnswerChoicesGrid
              choices={demoAnswerChoices}
              correctAnswer={demoPattern.quantity}
              wrongAnswer={null}
              hasAnswered={false}
              waitingForCorrectAnswer={false}
              disabled={true}
              onChoiceClick={() => {}}
              highlightCorrect={demoHighlightAnswer}
            />
          )}

          {/* Intro Phase - plays intro audio explaining the task */}
          {flashPhase === 'intro' && (
            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="mb-6"
              >
                <div className="text-6xl mb-4">👀</div>
                <div className="text-2xl lg:text-3xl text-gray-700 font-semibold mb-2">
                  Watch the screen!
                </div>
                <div className="text-lg text-gray-500">
                  I'll show you some dots...
                </div>
              </motion.div>
              {isTTSPlaying && (
                <div className="flex justify-center items-center text-gray-500">
                  <div className="animate-pulse mr-2">🔊</div>
                  <span>Listen carefully...</span>
                </div>
              )}
            </div>
          )}

          {/* Ready Phase - only shown on first question */}
          {flashPhase === 'ready' && isFirstQuestionRef.current && (
            <div className="text-center">
              <div className="text-2xl lg:text-3xl text-gray-600 animate-pulse mb-2">
                Get ready...
              </div>
              <div className="text-lg text-gray-500">Watch carefully!</div>
            </div>
          )}

          {/* Flashing Phase - Show Dots */}
          {flashPhase === 'flashing' && showDots && (
            <DotPatternDisplay dots={currentPattern.dots} isVisible={true} />
          )}

          {/* Blank Phase */}
          {flashPhase === 'blank' && (
            <div className="grid grid-cols-2 gap-8 lg:gap-12">
              <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
              <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
              <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
              <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
            </div>
          )}

          {/* Question Phase - Answer Choices */}
          {flashPhase === 'question' && (
            <AnswerChoicesGrid
              choices={answerChoices}
              correctAnswer={currentPattern.quantity}
              wrongAnswer={wrongAnswer}
              hasAnswered={hasAnswered}
              waitingForCorrectAnswer={waitingForCorrectAnswer}
              disabled={false}
              onChoiceClick={handleAnswerClick}
              selectedAnswer={selectedAnswer}
              neutralFeedbackMode={!isWarmup}
              minimalAnimation={!isFirstQuestionRef.current}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SubitizingQuestionScreen;
